// Pure display formatters for the analysis report. All tolerate null/undefined (the bundle's
// numbers are nullable — a metric the engine couldn't compute renders as an em dash, never "NaN").

const DASH = '—';

/** Whole-dollar currency, e.g. 37_180_000 → "$37,180,000". */
export function currency(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

/** Compact currency for big headline numbers, e.g. 40_350_000 → "$40.4M". */
export function currencyCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

/** A ratio (0.0597) rendered as a percent string, e.g. → "5.97%". `digits` controls precision. */
export function percentFromRatio(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${(value * 100).toFixed(digits)}%`;
}

/** An already-percent number (5.97) rendered with a trailing %, e.g. → "5.97%". */
export function percent(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${value.toFixed(digits)}%`;
}

/** A multiple, e.g. 1.31 → "1.31x". */
export function multiple(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${value.toFixed(digits)}x`;
}

/** A plain integer with thousands separators, e.g. 166 → "166". */
export function integer(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return Math.round(value).toLocaleString('en-US');
}

/** Basis points, e.g. 47 → "47 bps". */
export function basisPoints(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${Math.round(value)} bps`;
}

/** A short, human timestamp from an ISO string. Returns the dash on a bad/absent value. */
export function timestamp(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
